// v6 F11 — Robuste fetch wrapper voor auth-flows.
//
// Standaard `fetch()` gooit op iOS Safari "Load failed" / op Chrome
// "Failed to fetch" wanneer:
//   • backend cold-start (preview-omgeving > 30s wakker worden)
//   • netwerkonderbreking (4G→Wi-Fi switch op iPhone)
//   • TLS-handshake mislukt in een in-app webview
//   • CORS-preflight wordt geweigerd
//
// Deze helper:
//   1. Voegt een AbortController-timeout toe (default 15s)
//   2. Doet automatisch N retries bij netwerk-errors (NIET bij 4xx/5xx)
//   3. Klassificeert errors naar één van: 'timeout' | 'network' | 'auth' | 'server'
//   4. Logt naar console voor diagnostics, met een tag die in beta-feedback
//      direct herkenbaar is (`[emma:auth]`).

const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_RETRIES = 2;
const RETRY_BACKOFF_MS = 800;

export class AuthFetchError extends Error {
  constructor(kind, status, detail) {
    super(detail || kind);
    this.name = "AuthFetchError";
    this.kind = kind;            // 'timeout' | 'network' | 'auth' | 'server'
    this.status = status || 0;   // HTTP status (0 = network failure)
    this.detail = detail;        // optional backend message
  }
}

function _sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Robust fetch with timeout + retry. Resolves with the Response object on
 * 200..499. Rejects with an AuthFetchError on network errors or 5xx after
 * retries exhausted.
 */
export async function authFetch(url, init = {}, {
  timeoutMs = DEFAULT_TIMEOUT_MS,
  retries = DEFAULT_RETRIES,
  retryOn5xx = true,
} = {}) {
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const r = await fetch(url, {
        ...init,
        credentials: init.credentials || "include",
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (r.status >= 500 && retryOn5xx && attempt < retries) {
        console.warn(`[emma:auth] ${url} → ${r.status} (retry ${attempt + 1}/${retries})`);
        await _sleep(RETRY_BACKOFF_MS * (attempt + 1));
        continue;
      }
      return r;
    } catch (e) {
      clearTimeout(timer);
      const isAbort = e?.name === "AbortError";
      const kind = isAbort ? "timeout" : "network";
      lastErr = new AuthFetchError(kind, 0, e?.message || String(e));
      if (attempt < retries) {
        console.warn(`[emma:auth] ${url} → ${kind} (retry ${attempt + 1}/${retries})`);
        await _sleep(RETRY_BACKOFF_MS * (attempt + 1));
        continue;
      }
    }
  }
  throw lastErr || new AuthFetchError("network", 0, "Onbekende netwerkfout");
}

/**
 * Maps a Response to a user-friendly Dutch message. Use this in the UI
 * (LoginScreen, AuthCallback) so eindgebruikers nooit technische taal zien.
 */
export function describeAuthError(err) {
  if (!err) return "Er ging iets mis. Probeer opnieuw.";
  if (err instanceof AuthFetchError) {
    if (err.kind === "timeout") {
      return "De server reageert traag. Probeer opnieuw, of controleer je internetverbinding.";
    }
    if (err.kind === "network") {
      return "Geen verbinding. Controleer je internet en probeer opnieuw.";
    }
    if (err.kind === "server") {
      return "De server heeft een probleem. Probeer over enkele seconden opnieuw.";
    }
    if (err.kind === "auth") {
      return "Je sessie is verlopen. Log opnieuw in.";
    }
  }
  return err?.message || "Inloggen mislukt. Probeer opnieuw.";
}
